import { ReactComponent as Loader } from '@assets/svg/loader.svg';
import { ReactComponent as CloseImage } from '@assets/svg/small-cross.svg';
import { ReactComponent as TableSearch } from '@assets/svg/table-search.svg';
import TextInput from '@components/TextInput/TextInput';
import { Center, UnstyledButton } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { noop } from '@utils/noop';
import React, { forwardRef, memo } from 'react';
import { SearchFigmaProps, SearchFigmaType, UnstyledButtonType } from './types';
import {
  searchFigmaStyleDefaults,
  useSearchFigmaStyles,
} from './SearchFigma.styles';

function SearchFigma(
  props: SearchFigmaProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const {
    startSearch = noop,
    onChange = noop,
    onClear = noop,
    onKeyDown = noop,
    value,
    loading,
    classNames,
    ...rest
  } = props;
  const [searchText, handleChange] = useUncontrolled({
    value,
    defaultValue: '',
    onChange,
  });
  function onPressKeyboard(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      startSearch(searchText);
    }
    onKeyDown(event);
  }

  const { classes } = useSearchFigmaStyles(undefined, {
    classNames,
    name: 'SearchInput',
  });

  function onClearClick(): React.MouseEventHandler {
    return () => {
      if (loading) {
        return;
      }
      handleChange('');
      onClear();
    };
  }

  function onInputChange(): React.ChangeEventHandler<HTMLInputElement> {
    return e => {
      handleChange(e.target.value);
    };
  }
  function getIcon(): JSX.Element {
    if (loading) {
      return <Loader className="no-transitions" width={16} height={16} />;
    }
    return (
      <Center<UnstyledButtonType>
        w="100%"
        h="100%"
        component={UnstyledButton}
        onClick={onClearClick()}
      >
        <CloseImage width={16} height={16} />
      </Center>
    );
  }
  function showIcon() {
    if (loading) return true;
    return !!searchText;
  }

  return (
    <TextInput
      placeholder="Search"
      value={searchText}
      onChange={onInputChange()}
      onKeyDown={onPressKeyboard}
      autoFocus
      rightSectionWidth={searchFigmaStyleDefaults.rightSectionWidth}
      rightSection={showIcon() && getIcon()}
      iconWidth={searchFigmaStyleDefaults.iconWidth}
      icon={<TableSearch />}
      ref={ref}
      classNames={classes}
      {...rest}
    />
  );
}
export default memo(
  forwardRef<HTMLInputElement, SearchFigmaProps>(SearchFigma),
) as SearchFigmaType;
